/**
 * Inside Sales Training - Admin Analytics Dashboard
 * Comprehensive analytics for tracking trainee performance
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
    BarChart3, Users, Award, Clock, TrendingUp, 
    ChevronLeft, Trophy, Target, AlertTriangle,
    CheckCircle2, XCircle, User, Calendar
} from 'lucide-react';

const InsideSalesAnalytics = () => {
    const navigate = useNavigate();
    const { api, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [usersList, setUsersList] = useState([]);
    const [selectedTab, setSelectedTab] = useState('overview');

    useEffect(() => {
        if (!isAdmin) {
            navigate('/inside-sales');
            return;
        }
        fetchAnalytics();
    }, [isAdmin]);

    const fetchAnalytics = async () => {
        try {
            const [analyticsRes, usersRes] = await Promise.all([
                api.get('/inside-sales/admin/analytics'),
                api.get('/inside-sales/admin/users')
            ]);
            setAnalytics(analyticsRes.data);
            setUsersList(usersRes.data.users || []);
        } catch (error) {
            console.error('Failed to load analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 0.8) return 'text-emerald-500';
        if (score >= 0.6) return 'text-amber-500';
        return 'text-red-500';
    };

    const getScoreBadge = (score) => {
        if (score >= 0.8) return <Badge className="bg-emerald-500/10 text-emerald-600">Excellent</Badge>;
        if (score >= 0.6) return <Badge className="bg-amber-500/10 text-amber-600">Good</Badge>;
        return <Badge className="bg-red-500/10 text-red-600">Needs Work</Badge>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!isAdmin) {
        return null;
    }

    const sessionAnalytics = analytics?.session_analytics || [];
    const progressDist = analytics?.progress_distribution || {};

    // Calculate additional metrics
    const totalInProgress = progressDist['in_progress'] || 0;
    const totalCompleted = progressDist['completed'] || 0;
    const totalFailed = progressDist['failed'] || 0;

    // Find hardest and easiest sessions
    const sortedByScore = [...sessionAnalytics].sort((a, b) => a.average_score - b.average_score);
    const hardestSession = sortedByScore[0];
    const easiestSession = sortedByScore[sortedByScore.length - 1];

    return (
        <div className="space-y-6" data-testid="inside-sales-analytics">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/inside-sales')}
                        className="mb-2"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Back to Training
                    </Button>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <BarChart3 className="w-8 h-8 text-primary" />
                        Inside Sales Analytics
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Track trainee performance and program effectiveness
                    </p>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Enrolled</p>
                                <p className="text-3xl font-bold">{analytics?.total_users_with_access || 0}</p>
                            </div>
                            <Users className="w-10 h-10 text-blue-500" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Certified</p>
                                <p className="text-3xl font-bold">{analytics?.total_certified || 0}</p>
                            </div>
                            <Award className="w-10 h-10 text-emerald-500" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            {analytics?.certification_rate?.toFixed(1) || 0}% certification rate
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">In Progress</p>
                                <p className="text-3xl font-bold">{totalInProgress}</p>
                            </div>
                            <TrendingUp className="w-10 h-10 text-amber-500" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Sessions Completed</p>
                                <p className="text-3xl font-bold">{totalCompleted}</p>
                            </div>
                            <CheckCircle2 className="w-10 h-10 text-purple-500" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="sessions">Session Analytics</TabsTrigger>
                    <TabsTrigger value="trainees">Trainee Progress</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Insights */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Target className="w-5 h-5" />
                                    Key Insights
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {hardestSession && (
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                                        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                                        <div>
                                            <p className="font-medium text-sm">Hardest Session</p>
                                            <p className="text-sm text-muted-foreground">
                                                Session {hardestSession.session_id}: {hardestSession.title}
                                            </p>
                                            <p className="text-xs text-red-500 mt-1">
                                                Avg Score: {(hardestSession.average_score * 100).toFixed(0)}%
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {easiestSession && (
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                        <Trophy className="w-5 h-5 text-emerald-500 mt-0.5" />
                                        <div>
                                            <p className="font-medium text-sm">Easiest Session</p>
                                            <p className="text-sm text-muted-foreground">
                                                Session {easiestSession.session_id}: {easiestSession.title}
                                            </p>
                                            <p className="text-xs text-emerald-500 mt-1">
                                                Avg Score: {(easiestSession.average_score * 100).toFixed(0)}%
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                                    <Users className="w-5 h-5 text-blue-500 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-sm">Engagement</p>
                                        <p className="text-sm text-muted-foreground">
                                            {usersList.filter(u => u.sessions_completed > 0).length} trainees have started training
                                        </p>
                                        <p className="text-xs text-blue-500 mt-1">
                                            {usersList.filter(u => u.is_certified).length} have completed certification
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Progress Distribution */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5" />
                                    Progress Distribution
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span>Not Started</span>
                                            <span>{analytics?.total_users_with_access - usersList.filter(u => u.sessions_completed > 0).length}</span>
                                        </div>
                                        <Progress 
                                            value={((analytics?.total_users_with_access - usersList.filter(u => u.sessions_completed > 0).length) / (analytics?.total_users_with_access || 1)) * 100} 
                                            className="h-2 bg-muted"
                                        />
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-amber-600">In Progress</span>
                                            <span>{usersList.filter(u => u.sessions_completed > 0 && !u.is_certified).length}</span>
                                        </div>
                                        <Progress 
                                            value={(usersList.filter(u => u.sessions_completed > 0 && !u.is_certified).length / (analytics?.total_users_with_access || 1)) * 100} 
                                            className="h-2 [&>div]:bg-amber-500"
                                        />
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-emerald-600">Certified</span>
                                            <span>{analytics?.total_certified || 0}</span>
                                        </div>
                                        <Progress 
                                            value={analytics?.certification_rate || 0} 
                                            className="h-2 [&>div]:bg-emerald-500"
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Session Analytics Tab */}
                <TabsContent value="sessions">
                    <Card>
                        <CardHeader>
                            <CardTitle>Session Performance</CardTitle>
                            <CardDescription>
                                Average scores and completion rates by session
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {sessionAnalytics.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No session data yet. Trainees need to complete sessions first.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {sessionAnalytics.map((session) => (
                                        <div 
                                            key={session.session_id}
                                            className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                                        >
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold">
                                                {session.session_id}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{session.title}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {session.completions} completions
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`text-lg font-bold ${getScoreColor(session.average_score)}`}>
                                                    {(session.average_score * 100).toFixed(0)}%
                                                </p>
                                                <p className="text-xs text-muted-foreground">avg score</p>
                                            </div>
                                            <div className="w-32">
                                                <Progress 
                                                    value={session.average_score * 100} 
                                                    className="h-2"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Trainee Progress Tab */}
                <TabsContent value="trainees">
                    <Card>
                        <CardHeader>
                            <CardTitle>Trainee Progress</CardTitle>
                            <CardDescription>
                                Individual trainee performance tracking
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {usersList.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No trainees with Inside Sales access yet.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {usersList.map((user) => (
                                        <div 
                                            key={user.id}
                                            className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors border"
                                        >
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-semibold">
                                                {user.name?.charAt(0).toUpperCase() || 'U'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-medium truncate">{user.name}</p>
                                                    {user.is_certified && (
                                                        <Badge className="bg-emerald-500/10 text-emerald-600">
                                                            <Award className="w-3 h-3 mr-1" />
                                                            Certified
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {user.email}
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-lg font-bold">
                                                    {user.sessions_completed}/20
                                                </p>
                                                <p className="text-xs text-muted-foreground">sessions</p>
                                            </div>
                                            <div className="w-24">
                                                <Progress 
                                                    value={(user.sessions_completed / 20) * 100} 
                                                    className="h-2"
                                                />
                                            </div>
                                            {user.certification_date && (
                                                <div className="text-right text-xs text-muted-foreground">
                                                    <Calendar className="w-3 h-3 inline mr-1" />
                                                    {new Date(user.certification_date).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default InsideSalesAnalytics;
